import * as React from 'react';
// @ts-expect-error
import {Controller} from 'react-hook-form';
// @ts-expect-error
import type {Control, Watch} from 'react-hook-form';

import {CheckboxForGroup} from 'shells/checkbox/checkbox-for-group';
import {CheckboxGroup} from 'shells/checkbox/checkbox-group';

import type {FormFieldSubfieldsInput as Subfield} from 'nutshell-graphql-types';

import styles from './address-fields-checkbox-group.module.css';

type Props = {
    name: string;
    control: Control;
    watch: Watch;
    onEditAddressMultiFields: (updatedFields: Subfield[]) => void;
    isSmall?: boolean;
};

export const ADDRESS_FIELDS = {
    COUNTRY: 'country',
    ADDRESS_1: 'address_1',
    ADDRESS_2: 'address_2',
    CITY: 'city',
    STATE: 'state',
    POSTAL_CODE: 'postalCode',
} as const;

export function AddressFieldsCheckboxGroup(props: Props) {
    const subfields: Subfield[] = props.watch(`${props.name}.subfields`);
    const [checkedValues, setCheckedValues] = React.useState<string[]>([]);
    const [disableRemove, setDisableRemove] = React.useState(false);

    React.useEffect(() => {
        subfields.forEach((field) => {
            if (field.isEnabled) {
                setCheckedValues((prev) => [...prev, field.name]);
            }
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    React.useEffect(() => {
        if (
            checkedValues.length === 1 ||
            // we can infer that if address_2 is checked, address_1 is also checked
            (checkedValues.length === 2 && checkedValues.includes(ADDRESS_FIELDS.ADDRESS_2))
        ) {
            setDisableRemove(true);
        } else {
            setDisableRemove(false);
        }
    }, [checkedValues]);

    // @ts-expect-error
    const handleCheckboxChange = (newVals: string[], field) => {
        let updatedVals = newVals;

        if (
            !newVals.includes(ADDRESS_FIELDS.ADDRESS_1) &&
            checkedValues.includes(ADDRESS_FIELDS.ADDRESS_2)
        ) {
            // if address_1 is unchecked, uncheck address_2
            updatedVals = newVals.filter((val) => val !== ADDRESS_FIELDS.ADDRESS_2);
        }

        setCheckedValues(updatedVals);

        // newVal just contains the values of the checked checkboxes. When we update the fields, we have a list of objects, and wanna specifically update the isEnabled field of each object
        const updatedFields = subfields.map((formField) => {
            return {
                ...formField,
                isEnabled: updatedVals.includes(formField.name),
            };
        });

        props.onEditAddressMultiFields(updatedFields);
        field.onChange(updatedFields);
    };

    return (
        <div className='px-16 pb-16'>
            <div className='py-16'>
                Select the address fields you’d like to include in the form.
            </div>
            <Controller
                name={`${props.name}.subfields`}
                control={props.control}
                // @ts-expect-error
                render={({field}) => {
                    return (
                        <CheckboxGroup
                            name='addressFields'
                            onChange={(newVals: string[]) => {
                                handleCheckboxChange(newVals, field);
                            }}
                            selectedValues={checkedValues}
                        >
                            <CheckboxForGroup
                                disabled={
                                    disableRemove && checkedValues.includes(ADDRESS_FIELDS.COUNTRY)
                                }
                                value={ADDRESS_FIELDS.COUNTRY}
                                isLarge={!props.isSmall}
                            >
                                Country
                            </CheckboxForGroup>
                            <CheckboxForGroup
                                disabled={
                                    disableRemove &&
                                    checkedValues.includes(ADDRESS_FIELDS.ADDRESS_1)
                                }
                                value={ADDRESS_FIELDS.ADDRESS_1}
                                isLarge={!props.isSmall}
                            >
                                Street address 1
                            </CheckboxForGroup>
                            <div
                                data-tip={
                                    !checkedValues.includes(ADDRESS_FIELDS.ADDRESS_1)
                                        ? 'Street address 1 must be selected to select street address 2'
                                        : ''
                                }
                                className={styles['street-address-2-container']}
                            >
                                <CheckboxForGroup
                                    value={ADDRESS_FIELDS.ADDRESS_2}
                                    disabled={!checkedValues.includes(ADDRESS_FIELDS.ADDRESS_1)}
                                    isLarge={!props.isSmall}
                                >
                                    Street address 2
                                </CheckboxForGroup>
                            </div>
                            <CheckboxForGroup
                                disabled={
                                    disableRemove && checkedValues.includes(ADDRESS_FIELDS.CITY)
                                }
                                value={ADDRESS_FIELDS.CITY}
                                isLarge={!props.isSmall}
                            >
                                City
                            </CheckboxForGroup>
                            <CheckboxForGroup
                                disabled={
                                    disableRemove && checkedValues.includes(ADDRESS_FIELDS.STATE)
                                }
                                value={ADDRESS_FIELDS.STATE}
                                isLarge={!props.isSmall}
                            >
                                State
                            </CheckboxForGroup>
                            <CheckboxForGroup
                                disabled={
                                    disableRemove &&
                                    checkedValues.includes(ADDRESS_FIELDS.POSTAL_CODE)
                                }
                                value={ADDRESS_FIELDS.POSTAL_CODE}
                                isLarge={!props.isSmall}
                            >
                                Postal code
                            </CheckboxForGroup>
                        </CheckboxGroup>
                    );
                }}
            />
        </div>
    );
}
